import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { VideoContent } from './VideoContent';
import { PlaylistSection } from './PlaylistSection';
import { SearchBar } from './SearchBar';
import UploadModal from '../UploadModal';
import { Menu, Grid3X3, List, LayoutGrid, Upload, Youtube } from 'lucide-react';
import Context from '../../../../../Context/Context';

export default function Main() {
  const {
    userData,
    videos,
    setVideos,
    error,
    loading,
    fetchVideos,
    isDarkMode = true
  } = useContext(Context);

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [view, setView] = useState('grid');
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [initialVideos, setInitialVideos] = useState([]);
  const [inProgressStatus, setInProgress] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid, list, masonry

  const scrollableDivRef = useRef(null);

  useEffect(() => {
    // When videos are first fetched, store them as initial videos
    if (videos.length > 0 && initialVideos.length === 0) {
      setInitialVideos(videos);
    }
  }, [videos, initialVideos.length]);

  const resetToInitialVideos = () => {
    setVideos(initialVideos);
  };

  const filteredVideos = useMemo(() => {
    let filtered = videos;

    if (searchTerm) {
      filtered = filtered.filter(
        (video) =>
          video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          video.choreographer.toLowerCase().includes(searchTerm.toLowerCase()) ||
          video.songName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [videos, searchTerm]);

  // Event Handlers
  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setView('player');
    setSidebarOpen(false);
  };

  const handleClosePlayer = () => {
    setSelectedVideo(null);
    setView('grid');
    setSidebarOpen(false);
  };

  const handlePlaylistSelect = (playlist) => {
    setSelectedPlaylist(playlist);
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  const clearPlaylist = () => {
    setSelectedPlaylist(null);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className={`w-full h-full flex flex-col transition-colors duration-200 ${isDarkMode ? 'bg-black' : 'bg-white'}`} style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Main Container */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Hidden on mobile, shown on desktop */}
        <div className={`
          hidden md:flex md:relative z-30 h-full transition-all duration-300 ease-in-out
           flex-shrink-0
          ${isDarkMode ? 'bg-[#0f0f0f] border-r border-[#2a2a2a]' : 'bg-white border-r border-gray-200'}
        `}>
          <PlaylistSection
            selectedPlaylist={selectedPlaylist}
            handlePlaylistSelect={handlePlaylistSelect}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
          />
        </div>

        {/* Mobile Sidebar - Slide in from left */}
        <div className={`
          fixed md:hidden z-40 h-full transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          w-80 left-0
          ${isDarkMode ? 'bg-[#0f0f0f] border-r border-[#2a2a2a]' : 'bg-white border-r border-gray-200'}
        `}>
          <PlaylistSection
            selectedPlaylist={selectedPlaylist}
            handlePlaylistSelect={handlePlaylistSelect}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
          />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Header Section */}
          <div className={`w-full px-3 md:px-8 py-3 md:py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b flex-shrink-0 ${isDarkMode ? 'bg-[#0f0f0f] border-[#2a2a2a]' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-4 w-full sm:w-auto">
              <h1 className={`text-xl md:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-black'}`}>Choreography</h1>
              {selectedPlaylist && (
                <div className={`px-3 py-1 rounded-full ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-[#FDCF08]/10'}`}>
                  <span className={`text-xs md:text-sm font-medium ${isDarkMode ? 'text-[#FDCF08]' : 'text-[#FDCF08]'}`}>
                    {selectedPlaylist.playlistName}
                  </span>
                </div>
              )}
            </div>

            {/* Header Actions */}
            <div className="flex items-center gap-2 md:gap-3 w-full sm:w-auto">
              {/* Search Bar - Desktop */}
              <div className="hidden md:flex flex-1 max-w-md">
                <SearchBar
                  onSearchResults={(filteredVideos) => {
                    if (filteredVideos === null) {
                      resetToInitialVideos();
                    } else {
                      setVideos(filteredVideos);
                    }
                  }}
                  setSearchTerm={setSearchTerm}
                  searchTerm={searchTerm}
                />
              </div>

              {/* View Mode Toggle */}
              <div className={`flex items-center rounded-lg p-1 ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 md:p-2 rounded-md transition-all duration-200 ${viewMode === 'grid'
                      ? isDarkMode
                        ? 'bg-[#2a2a2a] text-[#FDCF08]'
                        : 'bg-white shadow-sm text-[#FDCF08]'
                      : isDarkMode
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  title="Grid"
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 md:p-2 rounded-md transition-all duration-200 ${viewMode === 'list'
                      ? isDarkMode
                        ? 'bg-[#2a2a2a] text-[#FDCF08]'
                        : 'bg-white shadow-sm text-[#FDCF08]'
                      : isDarkMode
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  title="List"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('masonry')}
                  className={`p-1.5 md:p-2 rounded-md transition-all duration-200 ${viewMode === 'masonry'
                      ? isDarkMode
                        ? 'bg-[#2a2a2a] text-[#FDCF08]'
                        : 'bg-white shadow-sm text-[#FDCF08]'
                      : isDarkMode
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  title="Masonry"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>

              {/* YouTube Button */}
              <button
                onClick={() => window.open("https://www.youtube.com/playlist?list=PLkl_0nrWH4iGcol0Y-itDyDRk0CffS1Q3", "_blank")}
                className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-xs md:text-sm ${isDarkMode
                    ? 'bg-[#1a1a1a] hover:bg-[#2a2a2a] text-red-400'
                    : 'bg-red-50 hover:bg-red-100 text-red-600'
                  }`}
              >
                <Youtube className="w-4 h-4" />
                <span className="font-medium">YouTube</span>
              </button>

              {/* Upload Button */}
              {(userData.userType === "admin" || userData.userType === "instructor") && (
                <button
                  onClick={() => {
                    if (!inProgressStatus) {
                      setIsModalOpen(true);
                    } else {
                      alert("Please wait for the upload to finish.");
                    }
                  }}
                  disabled={inProgressStatus}
                  className="px-3 md:px-4 py-2 bg-[#FDCF08] hover:bg-[#f0c000] disabled:bg-gray-400 disabled:hover:bg-gray-400 text-black rounded-lg transition-colors font-semibold text-xs md:text-sm flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  <span className="hidden md:block">Upload</span>
                </button>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={toggleSidebar}
                className={`md:hidden p-2 rounded-lg transition-colors ${isDarkMode
                    ? 'hover:bg-[#1a1a1a] text-white'
                    : 'hover:bg-gray-100 text-gray-700'
                  }`}
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Mobile Search Bar */}
          <div className="md:hidden px-3 py-3 border-b flex-shrink-0">
            <SearchBar
              onSearchResults={(filteredVideos) => {
                if (filteredVideos === null) {
                  resetToInitialVideos();
                } else {
                  setVideos(filteredVideos);
                }
              }}
              setSearchTerm={setSearchTerm}
              searchTerm={searchTerm}
            />
          </div>

          {/* Video Content Area */}
          <div className="flex-1 overflow-hidden">
            <VideoContent
              error={error}
              loading={loading}
              view={view}
              filteredVideos={filteredVideos}
              selectedVideo={selectedVideo}
              handleVideoClick={handleVideoClick}
              handleClosePlayer={handleClosePlayer}
              scrollableDivRef={scrollableDivRef}
              selectedPlaylist={selectedPlaylist}
              setVideos={setVideos}
              viewMode={viewMode}
              setViewMode={setViewMode}
              searchTerm={searchTerm}
            />
          </div>
        </div>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className={`fixed inset-0 z-30 md:hidden transition-opacity duration-300 ${isDarkMode ? 'bg-black/60' : 'bg-black/20'} backdrop-blur-sm`}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        setVideos={setVideos}
        setInProgress={setInProgress}
      />
    </div>
  );
}